/**
 * @fileoverview JkaggridComponent - 基於 AG Grid 的強大表格元件
 * @description
 * 此元件繼承自 JkGridBaseFatherComponent，主要用於建檔的資料顯示和操作功能。
 * 支援資料的顯示、編輯、過濾、排序和匯出等功能，是處理資料集的重要元件。
 *
 * @author JK Team
 * @version 2.0.0 - Upgraded to Angular 20
 */
import {
  Component,
  input,
  OnInit,
  output,
  ChangeDetectionStrategy,
  signal,
  effect
} from '@angular/core';
import { AgGridModule } from 'ag-grid-angular';
import {
  CellValueChangedEvent,
  ColDef,
  ColTypeDef,
  GridReadyEvent,
  RowEditingStartedEvent,
  RowEditingStoppedEvent,
} from 'ag-grid-community';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { JkBaseComponent, JkeditComponent, JkEntityV2, MaterialModule, jkLib } from 'jk-service';

import { JkgridwinComponent } from '../jkgridwin/jkgridwin.component';
import { AgGridService, GridInitOption, ColumnModi, } from 'jk-service';
import { entityState } from 'jk-service';
import { JkGridBaseFatherComponent } from '../jkgrid-base-father';

/**
 * @component JkaggridComponent
 * @description 強大的 AG Grid 表格元件，支援資料的增刪改查、過濾、排序和匯出功能
 * @extends {JkGridBaseFatherComponent}
 */
@Component({
  selector: 'app-jkaggrid',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(window:keydown)': 'onKeydown($event)'
  },
  imports: [
    AgGridModule,
    FormsModule,
    CommonModule,
    MaterialModule,
    JkeditComponent
  ],
  templateUrl: './jkaggrid.component.html',
  styleUrl: './jkaggrid.component.scss',
  providers: [AgGridService],
})
//export class JkaggridComponent extends JkgridwinComponent {
export class JkaggridComponent extends JkGridBaseFatherComponent {
  override isJkaggrid = true;
  srv!: JkEntityV2; // masterSrv
  get itemList(): any[] {
    if (this.srv)
      return this.srv.itemList()
    else
      return [];
  }
  set itemList(values: any[]) {
    this.srv.setItemList(values);
  }
  setItemList(values: any[]) {
    this.itemList = values;
    // 預留擴充
  }
  /**
   * @method calculateGridStyle
   * @description 計算網格樣式，包含高度控制邏輯
   * @returns {string} 計算後的 CSS 樣式字串
   */
  /*
  private calculateGridStyle(): string {
    let calculatedHeight = this.height() || this.defaultHeight();
    // 應用最小高度限制
    if (calculatedHeight < this.minHeight()) {
      calculatedHeight = this.minHeight();
    }
    // 應用最大高度限制

    if (this.maxHeight() && (calculatedHeight > this.maxHeight?.())) {
      calculatedHeight = this.maxHeight?.();
    }
    return `height: ${calculatedHeight}px; width: 100%;`;
  }
    */
  /**
   * @method calculateHeight
   * @description 計算網格高度，包含高度控制邏輯
   * @returns {number} 計算後的高度值
   */
  protected calculateHeight(): number {
    let calculatedHeight = this.height() || this.defaultHeight();
    // 應用最小高度限制
    if (calculatedHeight < this.minHeight()) {
      calculatedHeight = this.minHeight();
    }
    // 應用最大高度限制
    if (this.maxHeight() && calculatedHeight > this.maxHeight()) {
      calculatedHeight = this.maxHeight();
    }
    return calculatedHeight;
  }

  // Convert outputs to output() signals
  afterGridReadyAg = output<JkaggridComponent>();
  cellValueChanged = output<CellValueChangedEvent<any, any>>();

  /**
   * @property {Function} cbGridEdit - 網格編輯回調函數
   */
  cbGridEdit?: ($event: any) => void;
  /**
   * @property {Function} cbEditAndSave - 編輯並儲存回調函數
   */
  cbEditAndSave?: ($event: any) => void;
  /**
   * @property {Function} cbEdit - 編輯回調函數
   */
  cbEdit?: ($event: any) => void;
  /**
   * @property {Function} cbInsert - 新增回調函數
   */
  cbInsert?: ($event: any) => void;
  /**
   * @property {Function} cbDelete - 刪除回調函數
   */
  cbDelete?: ($event: any) => void;

  /**
   * @property {boolean} seekPanelVisible - 查詢面板的可見性
   */
  //seekPanelVisible: boolean = true;


  /**
   * @property {Object} columnTypes - 欄位類型定義
   */
  // columnTypes = input<{ [key: string]: ColTypeDef }>();

  /**
   * @property {boolean} showAdvanced - 是否顯示進階選項
   */
  showAdvanced = signal<boolean>(false);



  /**
   * @constructor
   * @description 建構元件實例並初始化 Excel 服務
   */
  constructor() {
    super();
    effect(() => {
      this._height.set(this.height());
    });
  }
  override ngOnInit() {
    super.ngOnInit();
    // itemListInput 的同步現在由 constructor 中的 effect 處理
    // 無需手動賦值，effect 會自動響應變化
  }

  /**
   * @method initPage
   * @description 初始化頁面配置
   * @param {GridInitOption} gridInitOpt - 網格初始化選項
   */
  initPage(gridInitOpt: GridInitOption, masterSrv: any) {
    this.initializeHeight();
    this.init(gridInitOpt);
    this.setGridColumns(masterSrv);
    this.srv = masterSrv;
  }

  /**
   * @method initializeHeight
   * @description 初始化高度設定
   */
  private initializeHeight(): void {
    // 如果沒有設定 height，使用 defaultHeight
    if (!this.height() || this.height() === this.defaultHeight()) {
      // 400 是原始預設值
      // Note: This would need to be handled differently in signals
    }
  }

  /**
   * @method setHeight
   * @description 設定網格高度，會自動應用最大/最小高度限制
   * @param {number} newHeight - 新的高度值
   */
  public setHeight(newHeight: number): void {
    // Note: This would need to be updated to work with signals
    if (this.gridSrv?.gridApi) {
      this.gridSrv.gridApi.sizeColumnsToFit();
    }
  }

  /**
   * @method setMaxHeight
   * @description 設定最大高度限制
   * @param {number} maxHeight - 最大高度值
   */
  public setMaxHeight(maxHeight: number): void {
    // Note: This would need to be updated to work with signals
    // 如果當前高度超過最大高度，調整為最大高度
    if (this.height() > maxHeight) {
      this.setHeight(maxHeight);
    }
  }

  /**
   * @method setMinHeight
   * @description 設定最小高度限制
   * @param {number} minHeight - 最小高度值
   */
  public setMinHeight(minHeight: number): void {
    // Note: This would need to be updated to work with signals
    // 如果當前高度小於最小高度，調整為最小高度
    if (this.height() < minHeight) {
      this.setHeight(minHeight);
    }
  }

  /**
   * @method onGridReady
   * @description 網格準備就緒事件處理
   * @param {GridReadyEvent} event - 網格準備就緒事件
   * @override
   */
  override onGridReady(event: GridReadyEvent) {
    super.onGridReady(event);
    if (this.afterGridReadyAg) {
      this.afterGridReadyAg.emit(this);
    }
  }
  override readonly afterGridReady = output<JkgridwinComponent>();
  /**
   * @method onCellValueChanged
   * @description 單元格值變更事件處理
   * @param {CellValueChangedEvent<any, any>} $event - 單元格值變更事件
   * @override
   */
  override onCellValueChanged($event: CellValueChangedEvent<any, any>) {
    super.onCellValueChanged($event);
    if (this.cellValueChanged) {
      this.cellValueChanged.emit($event);
    }
  }

  /**
   * @method onRowEditingStopped
   * @description 行編輯結束事件處理
   * @param {RowEditingStoppedEvent<any, any>} $event - 行編輯結束事件
   */
  override onRowEditingStopped($event: RowEditingStoppedEvent<any, any>) {
    super.onRowEditingStopped($event);
    console.log($event.rowIndex, $event.data);
    if (!$event.data) return;
    if ($event.rowIndex !== undefined) {
      if (!$event.rowIndex) return;
      let data = this.itemList[$event.rowIndex];
      if (this.cbEditAndSave) {
        this.cbEditAndSave(data);
      }
    }
  }

  /**
   * @method onRowEditingStarted
   * @description 行編輯開始事件處理
   * @param {RowEditingStartedEvent<any, any>} event - 行編輯開始事件
   */
  override onRowEditingStarted(event: RowEditingStartedEvent<any, any>) {
    super.onRowEditingStarted(event);
    console.log(event.rowIndex, event.data);
  }

  /**
   * @method doUIEdit
   * @description 觸發編輯操作
   */
  override doUIEdit() {
    super.doUIEdit();
    if (this.cbEdit) {
      this.cbEdit(undefined);
    }
  }

  /**
   * @method doUIInsert
   * @description 觸發新增操作
   */
  override doUIInsert() {
    super.doUIInsert();
    if (this.cbInsert) {
      this.cbInsert(undefined);
    }
  }

  /**
   * @method doUIGridEdit
   * @description 觸發網格編輯操作
   */
  doUIGridEdit() {
    if (this.cbGridEdit) {
      this.cbGridEdit(undefined);
    }
  }

  /**
   * @method doEditAndSave
   * @description 編輯並儲存操作
   */
  doEditAndSave() {
    if (this.cbEditAndSave) {
      this.cbEditAndSave(undefined);
    }
  }

  /**
   * @method doUIDelete
   * @description 觸發刪除操作
   */
  override doUIDelete() {
    super.doUIDelete();
    if (this.cbDelete) {
      this.cbDelete(undefined);
    }
  }

  override onKeydown(event: KeyboardEvent) {
    super.onKeydown(event);
    if (event.ctrlKey && event.shiftKey && event.key === 'E') {
      if (this.cbEdit) {
        this.cbEdit(undefined);
        event.preventDefault();
      }
    }
    if (event.ctrlKey && event.key === 'Insert') {
      if (this.cbInsert) {
        this.cbInsert(undefined);
      }
      event.preventDefault();
    }
    if (event.ctrlKey && event.key === 'Delete') {
      if (this.cbDelete) {
        this.cbDelete(undefined);
      }
      event.preventDefault();
    }
    if (event.ctrlKey && event.shiftKey && event.key === 'F') {
      this.doFilter('');
      event.preventDefault();
    }
  }

  async doCrud(state: entityState, item: any) {
    if (state === entityState.dsInsert) {
      await this.doInsert(item);
    }
    if (state === entityState.dsDelete) {
      await this.doDelete(item);
    }
    if (state === entityState.dsEdit) {
      this.doUpdate(item);
    }
    if (state === entityState.dsBrowse) {
      this.refresh();
    }
  }

  doInsert(item: any) {
    this.gridSrv.insert(item);
  }

  async doDelete(item: any): Promise<boolean> {
    let bool = await this.gridSrv.delete(item);
    if (!bool) {
      if (this.srv && this.srv.keyFields) {
        let matchedNode: any = null;
        this.gridSrv.gridApi.forEachNode((node) => {
          let match = true;
          for (let key of this.srv.keyFields) {
            if (node.data[key] !== item[key]) {
              match = false;
              break;
            }
          }
          if (match) {
            matchedNode = node;
          }
        });
        if (matchedNode) {
          let r = this.gridSrv.gridApi.applyTransaction({ remove: [matchedNode.data] });
          if (r?.remove.length === 1) {
            bool = true;
          }
        }
      }
    }
    return bool;
  }

  doUpdate(item: any) {
    let r = this.gridSrv.update(item);
    if (!r || r.update.length === 0) {
      if (this.srv && this.srv.keyFields) {
        let matchedNode: any = null;
        this.gridSrv.gridApi.forEachNode((node) => {
          let match = true;
          for (let key of this.srv.keyFields) {
            if (node.data[key] !== item[key]) {
              match = false;
              break;
            }
          }
          if (match) {
            matchedNode = node;
          }
        });
        if (matchedNode) {
          jkLib.cloneField(item, matchedNode.data);
          this.gridSrv.gridApi.applyTransaction({ update: [matchedNode.data] });
        }
      }
    }
  }

  showSelectedItem() {
    let data = this.gridSrv.selectedItem();
    this.doShowJson(data);
  }

  showAllItems() {
    this.doShowJson(this.itemList);
  }

  override doSetup() {
    super.doSetup();
    const pw = this.page.showInput('grid設定', '輸入管理員密碼', '', 'password')
      .subscribe((result) => {
        if (result === 'jenny2000') {
          this.showAdvanced.set(!this.showAdvanced());
        }
      });
  }

  editAndSave() { }

  onBtnExport() {
    var params = this.getParams();
    if (params.columnSeparator) {
      alert("NOTE: you are downloading a file with non-standard separators - it may not render correctly in Excel.");
    }
    this.gridSrv.gridApi.exportDataAsCsv(params);
  }

  getParams() {
    return {
      columnSeparator: this.getValue("#columnSeparator"),
    };
  }

  getValue(inputSelector: string) {
    var text = (document.querySelector(inputSelector) as any).value;
    switch (text) {
      case "none":
        return;
      case "tab":
        return "\t";
      default:
        return text;
    }
  }
  override itemListLength(): number {
    return this.itemList.length;
  }
  onSumAll() {
    this.gridSrv.onSumAll(this.itemList, this.columnDefs);
  }
  doItemList() {
    this.page
      .showInput('json內容', '輸入管理員密碼', '', 'password')
      .subscribe((pw: any) => {
        if (pw === 'jenny2000') {
          this.doShowJson(this.itemList);
        }
      });
  }
  //adjustHeight = input<number>(140);

  //height = signal<number>(window.innerHeight - 20);
  height = input<number>(this.defaultHeight() - 20);
  private _height = signal<number>(this.defaultHeight() - 20); // computed(() => this.height());
  heightChange(value: number) {
    let newH = value; // - this.adjustHeight();
    if (newH < this.minHeight()) newH = this.minHeight();
    if (this.maxHeight() && newH > this.maxHeight()) newH = this.maxHeight();
    this.gridStyle.height = newH + 'px';
    //console.log('gridStyle.height:', this.gridStyle.height);
    this.cdr.detectChanges();
    this._height.set(newH);
  }
  get heightValue(): number {
    return this._height();
  }
  maxHeight = input<number>(1000);
  get maxHeightValue(): number {
    return this.maxHeight();
  }
  minHeight = input<number>(200);
  override onWindowResize() {
    super.onWindowResize();
    //this.heightChange(window.innerHeight - this.adjustHeight());
  }
  //private _gridStyle: any;
  get gridStyle(): any {
    // const calculatedHeight = this.calculateHeight(); //calculateGridStyle();
    return this.gridSrv.gridStyle;
  }
  set gridStyle(value: any) {
    this.gridSrv.gridStyle = value;
    this.cdr.detectChanges();
  }
  setGridColumns(masterSrv: any) {
    let columnDefs: ColDef[] = [];
    let columnModi: ColumnModi[] = [];

    if (columnDefs.length === 0) {
      columnDefs = masterSrv.columnDefs;
    }
    columnDefs.push(...masterSrv.columnDefsVir);
    columnDefs.push(...this.gridSrv.managerFieldColDefs);
    //if (masterSrv.sortFieldList.length > 0)
    //  this.sortFields(masterSrv.sortFieldList); 整合到 ag-grid.service.ts 的 sortColumn
    this.gridSrv.doLoadTitle(columnModi);
    masterSrv.columnDefs = this.gridSrv.sortColumn(columnDefs, columnModi, masterSrv.sortFieldList);
    this.gridSrv.setFieldLengths(masterSrv.columnDefs, masterSrv.fieldLengths);
  }
  openwinItemStr(): string {
    let msg = ` 欄位數:${this.columnDefs.length}`;
    msg += 'height:' + this.gridStyle['height'];
    msg += 'width:' + this.gridStyle['width'];
    msg += 'minHeight:' + this.minHeight();
    msg += 'maxHeight:' + this.maxHeight();
    return msg;
  }
  override doShowInfo($event: MouseEvent) {
    super.doShowInfo($event);
    const pw = this.page
      .showInput('開窗設定', '輸入管理員密碼', '', 'password')
      .subscribe(async (pw: any) => {
        if (pw === 'jenny2000') {
          let msg = '開窗設定:' + this.openwinItemStr();
          await this.page.showMessage(msg);
        }
      });
  }
  gridHeightChange(n: number) {
    this.heightChange(this.heightValue + n);
  }
}
